import { UserCircle, Mail, Phone, ShieldCheck, Calendar } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { StudentLayout } from "@/components/student/layout/StudentLayout";
import { useStudentProfile } from "@/contexts/StudentProfileContext";
import { useCurrentUser } from "@/hooks/use-current-user";

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-800">{value}</span>
    </div>
  );
}

function SectionCard({ title, icon: Icon, children }: { title: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 bg-slate-50/60">
        <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
          <Icon className="w-3.5 h-3.5 text-indigo-600" />
        </div>
        <h2 className="text-sm font-bold text-slate-800">{title}</h2>
      </div>
      <div className="px-5 divide-y divide-slate-100">
        {children}
      </div>
    </div>
  );
}

export default function MyProfilePage() {
  usePageTitle('My Profile | BookMyCoaching');
  const { profile } = useStudentProfile();
  const { user } = useCurrentUser();

  const fullName = profile?.aboutMe?.fullName || user?.name || "Not set";
  const email = user?.email || "Not set";
  const phone = user?.phoneNumber || "Not set";

  const roles = user?.roles?.map((r) => r.role) ?? [];
  const defaultRole = user?.defaultRole ?? "Not set";

  const memberSince = profile?.createdDate
    ? new Date(profile.createdDate).toLocaleDateString(undefined, { year: "numeric", month: "long" })
    : user?.roles?.[0] ? "Active" : "Not set";

  const lastUpdated = profile?.updatedDate
    ? new Date(profile.updatedDate).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : "—";

  return (
    <StudentLayout>
      <main className="flex-1 min-h-screen overflow-auto">
        <div className="max-w-3xl px-4 sm:px-6 py-6 sm:py-8 space-y-5">

          {/* Header card */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="flex flex-col sm:flex-row">
              {/* Left */}
              <div className="relative flex-1 px-6 py-6 bg-indigo-50 overflow-hidden">
                <div className="absolute -top-6 -left-6 w-32 h-32 bg-indigo-400/10 rounded-full blur-2xl pointer-events-none" />
                <div className="absolute bottom-0 right-0 w-24 h-24 bg-violet-400/10 rounded-full blur-2xl pointer-events-none" />
                <div className="relative z-10">
                  <div className="flex items-center gap-1.5 mb-2">
                    <UserCircle className="w-3.5 h-3.5 text-indigo-600" />
                    <span className="text-xs font-bold uppercase tracking-widest text-indigo-600">My Profile</span>
                  </div>
                  {/* Avatar initials + name */}
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center text-white font-bold text-lg shrink-0">
                      {fullName !== "Not set"
                        ? fullName.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2)
                        : "?"}
                    </div>
                    <div>
                      <h1 className="text-xl sm:text-2xl font-black text-slate-900 leading-tight">{fullName}</h1>
                      <p className="text-sm text-slate-500 mt-0.5">{email}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right — stats */}
              <div className="flex items-center gap-4 sm:gap-5 px-5 sm:px-6 py-4 sm:py-5 border-t sm:border-t-0 sm:border-l border-slate-100 shrink-0">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-slate-800">{roles.length}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Roles</p>
                </div>
                <div className="w-px h-8 bg-slate-100" />
                <div className="text-center">
                  <p className="text-sm font-bold text-indigo-600 capitalize">{defaultRole.toLowerCase()}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Default</p>
                </div>
                <div className="w-px h-8 bg-slate-100" />
                <div className="text-center">
                  <p className="text-xs font-bold text-slate-800">{memberSince}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Member since</p>
                </div>
              </div>
            </div>
          </div>

          {/* Personal Info */}
          <SectionCard title="Personal Information" icon={UserCircle}>
            <InfoRow label="Full Name" value={fullName} />
            <InfoRow label="Email" value={email} />
            <InfoRow label="Phone" value={phone} />
          </SectionCard>

          {/* Account */}
          <SectionCard title="Account" icon={ShieldCheck}>
            <InfoRow label="Roles" value={roles.map(r => r.charAt(0) + r.slice(1).toLowerCase()).join(", ") || "Not set"} />
            <InfoRow label="Default Role" value={defaultRole.charAt(0) + defaultRole.slice(1).toLowerCase()} />
            <InfoRow label="Last Updated" value={lastUpdated} />
          </SectionCard>

        </div>
      </main>
    </StudentLayout>
  );
}
